import {
    buildGroupEditorCommonBindings,
    type GroupEditorCommonBindings,
} from '@/components/group-editor/group-editor-bindings';
import {
    GroupEditorDataBase,
    GroupEditorTargetBase,
    useGroupEditorAdapter,
    type UseGroupEditorAdapterArgs,
} from '@/components/group-editor/use-group-editor-adapter';

type GroupEditorDomainAdapterState<
    TGroupEditorTarget extends GroupEditorTargetBase,
    TGroupEditorData extends GroupEditorDataBase,
    TSelectionItem,
> = GroupEditorCommonBindings<TGroupEditorTarget, TGroupEditorData, TSelectionItem> & {
    pickedValues: string[];
    page: number;
    setPage: (page: number) => void;
    rowsOnPage: number;
    onRowsOnPageUpdate: (count: number) => void;
    totalRows: number;
    pageCount: number;
    pagedItems: TSelectionItem[];
    onUngroupSelected: () => Promise<void>;
};

type UseGroupEditorDomainAdapterArgs<
    TGroupEditorTarget extends GroupEditorTargetBase,
    TGroupEditorData extends GroupEditorDataBase,
    TSelectionItem,
    TRenameResponse extends { id: string; name: string },
    TExtractPayload extends Record<string, unknown>,
    TExtractResponse extends { sourceGroupDeleted: boolean },
    TDomainBindings,
> = {
    accessToken: string;
    extractApiErrorMessage: (error: unknown) => string | null;
    adapter: Omit<
        UseGroupEditorAdapterArgs<
            TGroupEditorTarget,
            TGroupEditorData,
            TSelectionItem,
            TRenameResponse,
            TExtractPayload,
            TExtractResponse
        >,
        'accessToken' | 'extractApiErrorMessage'
    >;
    mapDomainBindings: (args: {
        groupEditor: GroupEditorDomainAdapterState<TGroupEditorTarget, TGroupEditorData, TSelectionItem>;
        commonBindings: GroupEditorCommonBindings<TGroupEditorTarget, TGroupEditorData, TSelectionItem>;
    }) => TDomainBindings;
};

export const useGroupEditorDomainAdapter = <
    TGroupEditorTarget extends GroupEditorTargetBase,
    TGroupEditorData extends GroupEditorDataBase,
    TSelectionItem,
    TRenameResponse extends { id: string; name: string },
    TExtractPayload extends Record<string, unknown>,
    TExtractResponse extends { sourceGroupDeleted: boolean },
    TDomainBindings,
>({
    accessToken,
    extractApiErrorMessage,
    adapter,
    mapDomainBindings,
}: UseGroupEditorDomainAdapterArgs<
    TGroupEditorTarget,
    TGroupEditorData,
    TSelectionItem,
    TRenameResponse,
    TExtractPayload,
    TExtractResponse,
    TDomainBindings
>): TDomainBindings => {
    const groupEditor = useGroupEditorAdapter<
        TGroupEditorTarget,
        TGroupEditorData,
        TSelectionItem,
        TRenameResponse,
        TExtractPayload,
        TExtractResponse
    >({
        accessToken,
        extractApiErrorMessage,
        ...adapter,
    });
    const commonBindings = buildGroupEditorCommonBindings(groupEditor);

    return mapDomainBindings({
        groupEditor: {
            ...commonBindings,
            pickedValues: groupEditor.pickedValues,
            page: groupEditor.page,
            setPage: groupEditor.setPage,
            rowsOnPage: groupEditor.rowsOnPage,
            onRowsOnPageUpdate: groupEditor.onRowsOnPageUpdate,
            totalRows: groupEditor.totalRows,
            pageCount: groupEditor.pageCount,
            pagedItems: groupEditor.pagedItems,
            onUngroupSelected: groupEditor.onUngroupSelected,
        },
        commonBindings,
    });
};
